import type { ContextMenuItem } from "@design";
import { useToast } from "@design";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Download, GitFork, Share2, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  type ChatExportFormat,
  errorMessage,
  exportChat,
  forkSessionMutation,
  getExportCapabilitiesOptions,
} from "@/api";
import { saveBlob } from "@/lib/download";
import { type ChatHost, chatHost } from "./chatHost";
import { useDeleteChat } from "./useDeleteChat";

const FORMATS: ChatExportFormat[] = ["md", "docx", "pdf"];

/** Formats the server can produce right now (PDF needs LibreOffice). */
function availableFormats(capabilities: unknown): ChatExportFormat[] {
  const formats = (capabilities as { formats?: { id?: string; available?: boolean }[] } | undefined)?.formats;
  if (!Array.isArray(formats)) return ["md", "docx"];
  return FORMATS.filter((f) => formats.some((c) => c.id === f && c.available !== false));
}

/**
 * The open chat's "⋯" actions. Owners: share, export, delete. People it was
 * shared with: fork into their own chat, export.
 */
export function useChatActions({
  sessionUuid,
  title,
  isOwner,
  onShare,
  host = chatHost,
}: Readonly<{
  sessionUuid: string | undefined;
  title: string;
  isOwner: boolean;
  onShare: () => void;
  host?: ChatHost;
}>) {
  const { t } = useTranslation("chat", { keyPrefix: "actions" });
  const toast = useToast();
  const navigate = useNavigate();
  const deleteChat = useDeleteChat(() => navigate(host.newPath));
  const capabilities = useQuery({ ...getExportCapabilitiesOptions(), enabled: Boolean(sessionUuid) });
  const [exporting, setExporting] = useState(false);
  const fork = useMutation({
    ...forkSessionMutation(),
    onSuccess: (result) => {
      toast.success(t("forked"));
      navigate(host.pathFor(result.data.uuid));
    },
    onError: (error) => toast.error(errorMessage(error, t("forkFailed"))),
  });

  const runExport = async (format: ChatExportFormat) => {
    if (!sessionUuid || exporting) return;
    setExporting(true);
    try {
      const file = await exportChat(sessionUuid, format);
      saveBlob(file.blob, file.filename ?? `${title}.${format}`);
    } catch (error) {
      toast.error(errorMessage(error, t("exportFailed")));
    } finally {
      setExporting(false);
    }
  };

  if (!sessionUuid) return { items: [] as ContextMenuItem[], dialog: null, forking: false, fork: () => {} };
  const items: ContextMenuItem[] = [
    ...(isOwner ? [{ label: t("share"), icon: <Share2 size={16} />, onSelect: onShare }] : []),
    ...(!isOwner
      ? [
          {
            label: t("fork"),
            icon: <GitFork size={16} />,
            disabled: fork.isPending,
            onSelect: () => fork.mutate({ path: { session_uuid: sessionUuid } }),
          },
        ]
      : []),
    ...availableFormats(capabilities.data).map((format) => ({
      label: t(`export.${format}`),
      icon: <Download size={16} />,
      disabled: exporting,
      onSelect: () => void runExport(format),
    })),
    ...(isOwner
      ? [
          {
            label: t("delete"),
            icon: <Trash2 size={16} />,
            danger: true,
            onSelect: () => deleteChat.onDelete({ uuid: sessionUuid, name: title }),
          },
        ]
      : []),
  ];
  return {
    items,
    dialog: deleteChat.dialog,
    forking: fork.isPending,
    fork: () => fork.mutate({ path: { session_uuid: sessionUuid } }),
  };
}
